import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError
}

export function useOffline(): { offline: boolean; retry: () => Promise<void> } {
  const queryClient = useQueryClient()
  const [offline, setOffline] = useState(() => !navigator.onLine)

  const retry = useCallback(async (): Promise<void> => {
    try {
      await queryClient.refetchQueries({ type: 'active' })
    } catch {
      setOffline(true)
      return
    }
    setOffline(
      !navigator.onLine ||
        queryClient
          .getQueryCache()
          .findAll({ type: 'active' })
          .some((query) => isNetworkError(query.state.error)),
    )
  }, [queryClient])

  useEffect(() => {
    const onOnline = (): void => {
      setOffline(false)
      queryClient.refetchQueries({ type: 'active' }).catch(() => setOffline(true))
    }
    const onOffline = (): void => setOffline(true)
    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return
      const { status, error } = event.query.state
      if (status === 'error' && isNetworkError(error)) setOffline(true)
    })
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return
      const { status, error } = event.mutation.state
      if (status === 'error' && isNetworkError(error)) setOffline(true)
    })
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      unsubscribeQueries()
      unsubscribeMutations()
    }
  }, [queryClient])

  return { offline, retry }
}
